// lib/leasing/sourceFocusedBrand.ts
//
// "Which brand is the user looking at in Source?", resolved from the one slice that already
// answers it, `sourceFocusedFitId`, published by the expanded shortlist row
// (SourcingCenterView's setFocusedFit) so the right bar can swap to the brand panel.
//
// Two callers need the same answer and were drifting apart: the composer's subject chip, which
// tells the user which brand the agent will act on, and ENRICH_BRAND, which acts on it. The
// command resolved the row against the PortfolioFit registry ALONE, so on an asset shortlist ,
// where the rows are Leads, the common case, it found nothing and refused with "brandId is
// required unless a brand is open in the Source editor" while a brand was plainly open. A chip
// that promises a subject the command cannot find is worse than no chip.
//
// The three registries are checked in the same order SourceBrandPanel checks them, because a
// fit id is unique across all three and the panel is the surface this chip describes.

/** The shape both callers can supply: the kernel store, or a getState() snapshot. */
type FocusStateLike = {
  sourceFocusedFitId?: string | null;
  sourceSelectedFitIds?: unknown;
  inventory?: {
    PortfolioFit?: { registry?: Record<string, unknown> };
    Lead?: { registry?: Record<string, unknown> };
  };
};

const brandIdOf = (row: unknown): string => {
  if (!row || typeof row !== 'object') return '';
  const value = (row as { brandId?: unknown }).brandId;
  return typeof value === 'string' ? value.trim() : '';
};

const leadIdOf = (row: unknown): string => {
  if (!row || typeof row !== 'object') return '';
  const value = (row as { leadId?: unknown }).leadId;
  return typeof value === 'string' ? value.trim() : '';
};

/**
 * The brandId of the shortlist row currently open in Source, or '' when none is.
 * Never throws: a missing registry or a stale id resolves to ''.
 */
export const resolveSourceFocusedBrandId = (state: FocusStateLike | null | undefined): string => {
  const focused = typeof state?.sourceFocusedFitId === 'string' ? state.sourceFocusedFitId.trim() : '';
  // A SINGLE TICKED ROW counts too. SourceBrandPanel opens on it, so the user is looking at that
  // brand just as much as at an expanded one, and answering '' here made the chip promise a
  // subject the commands could not find - the same drift this resolver exists to end.
  const selected = Array.isArray(state?.sourceSelectedFitIds) ? state.sourceSelectedFitIds : [];
  const onlySelected =
    selected.length === 1 && typeof selected[0] === 'string' ? selected[0].trim() : '';
  const fitId = focused || onlySelected;
  if (!fitId) return '';
  const inventory = state?.inventory;
  const row =
    inventory?.PortfolioFit?.registry?.[fitId] ??
    inventory?.Lead?.registry?.[fitId];
  const direct = brandIdOf(row);
  if (direct) return direct;
  // A row that carries only the Lead it was scored from still has to resolve a brand: same hop
  // SourceBrandPanel and SourceBulkEnrichPanel already make.
  const leadId = leadIdOf(row);
  if (!leadId) return '';
  return brandIdOf(inventory?.Lead?.registry?.[leadId]);
};

/**
 * Every shortlist row the user has TICKED, as { fitId, brandId } pairs, in the order the store
 * holds them. Empty when nothing is selected.
 *
 * Same registries and the same Lead hop as the single-row resolver above, because a ticked row and
 * an expanded row are the same kind of row; only the count differs. Rows that no longer resolve to
 * a brand (a stale id, a registry not hydrated yet) are dropped rather than reported as a brand
 * with an empty name: the caller prints this to the agent, and a blank entry there reads as a
 * brand the user selected and we lost.
 */
export const resolveSourceSelectedBrandIds = (
  state: FocusStateLike | null | undefined,
): Array<{ fitId: string; brandId: string }> => {
  const selected = Array.isArray(state?.sourceSelectedFitIds) ? state.sourceSelectedFitIds : [];
  const inventory = state?.inventory;
  const out: Array<{ fitId: string; brandId: string }> = [];
  for (const raw of selected) {
    const fitId = typeof raw === 'string' ? raw.trim() : '';
    if (!fitId) continue;
    const row =
      inventory?.PortfolioFit?.registry?.[fitId] ??
      inventory?.Lead?.registry?.[fitId];
    const direct = brandIdOf(row);
    if (direct) {
      out.push({ fitId, brandId: direct });
      continue;
    }
    const leadId = leadIdOf(row);
    const viaLead = leadId ? brandIdOf(inventory?.Lead?.registry?.[leadId]) : '';
    if (viaLead) out.push({ fitId, brandId: viaLead });
  }
  return out;
};
